import React, {
  ButtonHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { getOtp, OtpRequest, OtpResponse } from "@utils/api/account";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

interface RequestOtpButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "onClick" // onClick is controlled by the component
  > {
  children: ReactNode;

  buildOtpRequest: () => OtpRequest;
  // countDownAppearance modifies the appearance of the button
  // during OTP sent countdown
  countDownAppearance: {
    buildChildren: (timeLeftSecond: number) => ReactNode;
    className: string;
  };

  // handleOtpRateLimit is called when the OTP request is rate limited
  handleOtpRateLimit?: (resp: OtpResponse) => void;
  // handleOtpRequestError is called when the OTP request fails
  handleOtpRequestError?: (e: any) => void;

  otpApi?: (params: OtpRequest, t: TFunction) => Promise<OtpResponse>;
}

const buttonRefreshIntervalMs = 1000;

export default function OtpButton({
  children,
  className,
  buildOtpRequest,
  countDownAppearance,
  handleOtpRateLimit,
  handleOtpRequestError,
  otpApi = getOtp,
  ...otherProps
}: RequestOtpButtonProps) {
  const [requestSent, setRequestSent] = useState(false);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeLeftSecond, setTimeLeftSecond] = useState(0); // Store the countdown in state
  const timer = useRef<NodeJS.Timeout | null>(null);

  const { t } = useTranslation();

  const resetAllState = () => {
    if (timer.current) {
      clearInterval(timer.current!);
    }
    setRequestSent(false);
    setExpiryTime(null);
    setTimeLeftSecond(0);
  };

  const refreshCountdown = () => {
    if (requestSent && expiryTime) {
      timer.current = setInterval(() => {
        if (expiryTime.getTime() > Date.now()) {
          setTimeLeftSecond(timeDiffSecond(expiryTime.getTime())); // Update the countdown
        } else {
          resetAllState(); // timeout stale
        }
      }, buttonRefreshIntervalMs);
    }

    return () => {
      if (timer.current) {
        clearInterval(timer.current!);
      }
    }; // clean up the timer when the component is unmounted / dependencies change
  };
  useEffect(refreshCountdown, [requestSent, expiryTime]);

  const sendOTP = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const resp = await otpApi(buildOtpRequest(), t);

      if (!resp.sent) {
        // otp did not send to the user's mobile
        handleOtpRateLimit?.(resp);
        return;
      }

      // success
      setRequestSent(true);
      setExpiryTime(new Date(resp.nextRetryUnixMs));
      setTimeLeftSecond(timeDiffSecond(resp.nextRetryUnixMs));
    } catch (e) {
      handleOtpRequestError?.(e);
      return;
    }
  };

  return (
    <button
      onClick={sendOTP}
      className={requestSent ? countDownAppearance.className : className}
      {...otherProps}
    >
      {requestSent
        ? countDownAppearance.buildChildren(timeLeftSecond)
        : children}
    </button>
  );
}

function timeDiffSecond(timestampMs: number): number {
  return Math.ceil((timestampMs - Date.now()) / 1000);
}
